import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GetTeamDocument } from "#app/lib/data";
import { toColor, toGlyphName, type Color, type GlyphName } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

/**
 * `Team.icon` is nullable and its parts are free-form strings, but the icon
 * picker needs concrete values both to render and to send back on change. The
 * resolution lives once here so every editor of a team's appearance (the
 * settings page, the masthead menu) agrees with the entity visual.
 *
 * `icon: null` IS a state, not a gap: a team with no stored glyph renders its
 * name's initial on the color tile (the monogram), and the picker's "Use
 * initial" cell selects it by reporting `null`. Substituting the kind glyph
 * here would make every color change silently convert a monogram team into a
 * glyph team.
 */
export type TeamGlyph = { icon: GlyphName | null; color: Color };

export function resolveTeamGlyph(
  team: { icon?: { icon?: string | null; color?: string | null } | null } | null | undefined,
): TeamGlyph {
  return {
    icon: toGlyphName(team?.icon?.icon) ?? null,
    color: toColor(team?.icon?.color) ?? "dark-grey",
  };
}

type UseTeamDetailOptions = Omittable<{
  /** Additional skip condition (query already skips when teamId is falsy) */
  skip?: boolean;
  /** Hover previews pass "cache-first" so pointer travel doesn't spam network. */
  fetchPolicy?: "cache-and-network" | "cache-first";
}>;

export function useTeamDetail(teamId: string | undefined, options?: UseTeamDetailOptions) {
  const queryVariables = useMemo(() => ({ id: teamId! }), [teamId]);

  const { data, loading, error } = useQuery(GetTeamDocument, {
    variables: queryVariables,
    fetchPolicy: options?.fetchPolicy ?? "cache-and-network",
    skip: !teamId || (options?.skip ?? false),
  });

  const team = data?.team ?? null;

  const glyph = useMemo<TeamGlyph>(() => resolveTeamGlyph(team), [team]);

  return { team, glyph, loading, error };
}
